import { useLayoutEffect, type RefObject } from "react";

type OverlayAlign = "start" | "end" | "stretch";

export function useAnchoredOverlay({
  open,
  triggerRef,
  overlayRef,
  gap = 6,
  margin = 8,
  align = "stretch",
  minHeight = 120,
  matchTriggerWidth = align === "stretch",
}: {
  open: boolean;
  triggerRef: RefObject<HTMLElement | null>;
  overlayRef: RefObject<HTMLElement | null>;
  gap?: number;
  margin?: number;
  align?: OverlayAlign;
  minHeight?: number;
  matchTriggerWidth?: boolean;
}) {
  useLayoutEffect(() => {
    if (!open) return;

    const place = () => {
      const trigger = triggerRef.current;
      const overlay = overlayRef.current;
      if (!trigger || !overlay) return;

      overlay.style.visibility = "hidden";
      overlay.style.position = "fixed";
      overlay.style.top = "0px";
      overlay.style.left = "0px";
      overlay.style.right = "auto";
      overlay.style.bottom = "auto";
      overlay.style.maxHeight = "none";
      if (matchTriggerWidth) {
        overlay.style.width = `${trigger.getBoundingClientRect().width}px`;
      }

      const rect = trigger.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const naturalWidth = overlay.offsetWidth;
      const width = Math.min(
        Math.max(naturalWidth, matchTriggerWidth ? rect.width : 0),
        vw - margin * 2,
      );

      let left = align === "end" ? rect.right - width : rect.left;
      left = Math.min(Math.max(left, margin), vw - margin - width);

      const spaceBelow = vh - rect.bottom - gap - margin;
      const spaceAbove = rect.top - gap - margin;
      const needed = overlay.offsetHeight;
      const placeBelow =
        spaceBelow >= Math.min(needed, minHeight) || spaceBelow >= spaceAbove;
      const maxHeight = Math.max(80, placeBelow ? spaceBelow : spaceAbove);

      overlay.style.width = `${width}px`;
      overlay.style.maxHeight = `${maxHeight}px`;
      overlay.style.overflowY = "auto";
      overlay.style.left = `${left}px`;
      if (placeBelow) {
        overlay.style.top = `${rect.bottom + gap}px`;
        overlay.style.bottom = "auto";
      } else {
        overlay.style.top = "auto";
        overlay.style.bottom = `${vh - rect.top + gap}px`;
      }
      overlay.style.visibility = "visible";
    };

    place();
    const frame = window.requestAnimationFrame(place);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [
    open,
    triggerRef,
    overlayRef,
    gap,
    margin,
    align,
    minHeight,
    matchTriggerWidth,
  ]);
}
